import { HttpClient } from '@angular/common/http';
import {Injectable, Pipe, PipeTransform, signal} from '@angular/core';
import { OrderService } from 'src/app/_services/order.service';
import { AppService } from 'src/app/app.service';
import {ChatBasketService} from "./chat-basket.service";
import {Observable} from "rxjs";

@Injectable({ providedIn: 'root' })
export class ChatService {
    private readonly apiUrl;
	STORAGE_KEY_SESSION_ID = 'chatbot-session-id';
	STORAGE_KEY_MESSAGES = 'chatbot-messages';

    private messagesSignal = signal<ChatMessage[]>(this.loadMessagesFromStorage());
    sessionId = signal(this.loadSessionIdFromStorage() ?? 'new');

    // Signal for system order requests - one order per session
    systemOrderRequest = signal<SystemOrderRequest | null>(null);

	loading = signal(false);
    cancelController: AbortController | null = null;
    messagePrefix = 'Answer only in ';
	$storage: any;

	questions: any= [
		{id: 'mealPlan', text: 'Meal recommendation', searchPattern:`MealRecommendationPattern`, fields: [
			// number count toggle
			{id: 'noAdults', text: 'Number of adults', type: 'count', min: 0, max: 5, step: 1, default: 2, required: true},
			{id: 'noChildren', text: 'Number of children', type: 'count', min: 0, max: 5, step: 1, default: 0, required: true},
			{id: 'budget', text: 'Budget', type: 'amount', min: 100, max: 600, step: 20, default: 300, required: true},
			],
			budget: {adults: 10, children: 10},
		},

		{id: 'menuHelp', text: 'Find by properties', searchPattern: 'FindByPropsPattern', fields: [
				{id: 'category', text: 'Categories', type: 'chips', selectionType: 'single', onEmpty: 'כל הסוגים', members: [
					{value: 'Firsts'},
					{value: 'Mains'},
					{value: 'Salads'},
					{value: 'Drinks'},
					{value: 'Deserts'},
					{value: 'Soups'},
					{value: 'Sushi'},
				]},
				{id: 'characteristics', text: 'Properties', type: 'chips', selectionType: 'multi', required: true, members: [
					{value: 'Vegetarian'},
					{value: 'Spicy'},
					{value: 'Vegan'},
					{value: 'Gluten free'},
				]},
			]},
		{id: 'searchByPrice', text: 'Find by price', searchPattern: 'FindByPricePattern', fields: [
			{id: 'budget', text: 'Up to a price of', type: 'amount', min: 50, max: 300, step: 10, default: 50, required: true},
		]},
	];
	outOfStockParsed: string[];

    constructor(
		private http: HttpClient,
		private appService: AppService,
		private orderService: OrderService,
		private chatBasketService: ChatBasketService
	) {
		this.$storage = this.orderService.$storage;
		this.orderService.getCatalogItemsAsFlatArray();
		this.outOfStockParsed = this.orderService.catalogItemsAsFlatArray.filter(o => o.stockDisabled == true).map(o => o.offer);
		this.apiUrl = `${this.appService.appConfig.waiterChatbotAPI}/api/conversation/`;

		const mealBudget = this.$storage.catalog.botConfig?.promptOverrides?.budget;
		let mealPlan = this.questions.find(q => q.id === 'mealPlan');
		if (mealPlan && mealBudget) {
			mealPlan.budget = {...mealPlan.budget, ...mealBudget}
		}

	}

	translateKey(value, args?: any): string {
		const key = `_QK.${value}`;
		let translation, translationKeys = this.appService.aiLocale.keys;
		if (translationKeys) {
			translation = this.appService.translateKey(translationKeys, key, args);
		}
		if (!translation) {
			translation = this.appService.translateKey(this.appService.translations, key, args);
		}
		return translation || value;
	}

    // -------------------------------------------------------------------------------------------->
    // -------------------------------------------------------------------------------------------->

    async initChatbot(): Promise<void> {
		const isNewSession = this.getSessionId() === 'new';
		const basketMessage = this.chatBasketService.getBasketChatMessage();
		if (basketMessage) return this.sendMessage(basketMessage);
		if (isNewSession)  return this.sendMessage('');
    }

	async updateBasket() {
		const basketMessage = this.chatBasketService.getBasketChatMessage();
		if (basketMessage) return this.sendMessage(basketMessage);
	}

    async sendMessage(message?: string): Promise<any> {
        try {
            this.cancelController.abort('Cancelling Controller');
        } catch (error) {
            console.debug('AbortController already aborted:', error);
        }
        this.cancelController = new AbortController();
		this.loading.set(true);
        try {
            const stream = await this.sendMessageStream(message, this.cancelController.signal);
            const reader = stream.getReader();
            const decoder = new TextDecoder('utf-8');
            let fullText = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                fullText += chunk;
            }
        } catch (err: any) {
            if (err?.name !== 'AbortError') {
                console.error('Chat error:', err);
            }
        } finally {
            this.loading.set(false);
            this.cancelController = null;
        }
    }

	sendMessageStream(message: string, signal?: AbortSignal): Promise<ReadableStream<Uint8Array>> {
		const body = this.buildChatRequestBody(message);
		return this.doFetch('stream-chat', body, signal).then((res) => {
			if (!res.ok) {
				throw new Error(`HTTP error! status: ${res.status}`);
			}
			let sessionId = res.headers.get('X-Session-ID');
			if (sessionId) {
				sessionId = sessionId.replace(/"/g, '');
				this.updateSessionId(sessionId);
			}
			if (!res.body) {
				throw new Error('No response body received from server');
			}
			return res.body;
		}).catch((error) => {
			console.error('Error in sendMessageStream:', error);
			throw error;
		});
	}

	private buildChatRequestBody(message: string): string {
		let request: any = {
			message: `${message}`,
			sessionId: this.sessionId(),
			timezone: "Asia/Jerusalem",
			serviceType: this.$storage.service,
			locale: this.appService.aiLocale.id,
			skipWelcomeMessage: true,
		};
		if (this.sessionId() === 'new') {
			request = {
				...request,
				organization: this.orderService?.$storage?.siteId || '',
				outOfStock: this.outOfStockParsed,
				historyDetails: {},
				timestamp: new Date().toISOString(),
			};
		}
		return JSON.stringify(request);
	}

    cancelOngoingRequest(): void {
        if (this.cancelController && !this.cancelController.signal.aborted) {
            try {
                this.cancelController.abort('Component destroyed');
            } catch (error) {
                console.debug('AbortController already aborted:', error);
            }
        }
        this.cancelController = null;
        this.loading.set(false);
    }

	transcribeAudio(request: TranscriptionRequest): Observable<TranscriptionResponse> {
		const formData = new FormData();
		formData.append('audio', request.audio, 'voice-message.wav');
		formData.append('duration', request.duration.toString());
		formData.append('sessionId', request.sessionId);
		formData.append('timestamp', request.timestamp);
		formData.append('locale', this.appService.aiLocale.id,);
		const endpoint = 'https://speech-to-text.tabit-int.com/transcribe';
		return this.http.post<TranscriptionResponse>(endpoint, formData);
	}

    // -------------------------------------------------------------------------------------------->
    // -------------------------------------------------------------------------------------------->

    getSessionId(): string {
        return this.sessionId();
    }

	cacheSessionAiChat(key, value, isSessionId = false) {
		this.orderService.$storage[key] = value;
		this.orderService.cacheOrder();
	}

	getItemAiChat(key) {
		return this.orderService.$storage[key];
	}

    updateSessionId(id: string) {
        this.sessionId.set(id);
        this.cacheSessionAiChat(this.STORAGE_KEY_SESSION_ID, id, true);
    }

    private loadSessionIdFromStorage(): string | null {
        try {
            return this.getItemAiChat(this.STORAGE_KEY_SESSION_ID);
        } catch {
            return null;
        }
    }

    private loadMessagesFromStorage(): ChatMessage[] {
        try {
            const raw = this.getItemAiChat(this.STORAGE_KEY_MESSAGES);
            const messages = raw || [];
            if (!Array.isArray(messages)) {
                console.warn('Invalid messages format in storage:', messages);
                return [];
            }
            const validMessages = messages.filter(msg =>
                msg &&
                typeof msg === 'object' &&
                typeof msg.role === 'string' &&
                typeof msg.content === 'string'
            );
        } catch (error) {
            console.warn('Error loading messages from storage:', error);
            return [];
        }
    }

  	//--------------------------------------------------------------------------------------------->
	//--------------------------------------------------------------------------------------------->

	doFetch(url, body, signal?) {
		const token = localStorage.getItem('token') || sessionStorage.getItem('token');
		return fetch(this.apiUrl + url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			body,
			signal,
		})
	}
}

@Pipe({
	name: 'cTranslate',
	pure: false,
	standalone: true
})
export class TranslateChatPipe implements PipeTransform {
	constructor(private chatService: ChatService) {}
	transform(value: any, args?: any): any {
		return this.chatService.translateKey(value, args);
	}
}

export type ChatMessageType = 'normal' | 'processing' | 'error' | 'feedback' | 'empty' | 'voice';

export interface ChatMessage {
	role: 'user' | 'assistant' | 'assistant-first' | 'assistant-temporary';
	content: string;
	type?: ChatMessageType;
	direction?: 'ltr' | 'rtl';
	hidden?: boolean;
	offers?: any[],
	processed?: boolean,
	provisional?: boolean,
}

export interface OfferModifier {
	modifierGroupId: string;
	modifierId: string;
	modifier: string;
	action?: string;
}
export interface ImageDetails {
	name: string;
	description: string;
	imageURL: string;
}

export interface OfferSelection {
	item: string;
	name: string;
	price?: number;
	itemGroup: string;
	itemGroupName?: string;
	selectedModifiers?: OfferModifier[];
}

export interface Offer {
	readonly offer: string;
	readonly name: string;
	readonly price?: number;
	readonly selectedModifiers?: OfferModifier[];
	readonly removedModifiers?: OfferModifier[];
	readonly selections?: OfferSelection[];
}

export type ChatOrderStatusCode = 'processing' | 'completed' | 'error' | 'empty' | 'feedback';

export interface ChatOrderResponse {
	textResponse: string;
	isOfferValid: boolean;
	offers?: Offer[];
	statusCode?: ChatOrderStatusCode;
}

export interface UpdateUserChangesRequest {
	sessionId: string;
	menu: string;
	updateMessage: string;
}

export interface OrderItem {
	offerId: string;
	quantity: number;
	modifications: Array<{
		action?: any;
		modificationId: string;
		choice: string;
	}>;
	selections: Array<{
		action?: any;
		selectionId: string;
		choice: string;
		modifications: Array<{
			action?: any;
			modificationId: string;
			choice: string;
		}>;
	}>;
}

export interface SystemOrderRequest {
	System_request_order: OrderItem[];
}

export interface TranscriptionRequest {
	audio: Blob;
	duration: number;
	sessionId: string;
	timestamp: string;
}

export interface TranscriptionResponse {
	success: boolean;
	text?: string;
	error?: string;
}
